import { useRef, useState } from "react";
import html2canvas from "html2canvas-pro";
import {useGameConfiguration} from "@/state/cacheStoreHooks";

export function useShareAsImage() {
    const ref = useRef<HTMLDivElement>(null);
    const [cachedCanvas, setCachedCanvas] = useState<HTMLCanvasElement | null>(null);
    const [isPreparing, setIsPreparing] = useState(false);
    const [debugUrl, setDebugUrl] = useState<string | null>(null);
    const gameConfiguration = useGameConfiguration();
    const baseFileName = gameConfiguration?.shareImageFilename || "share";
    const captureImage = async () => {
        if (!ref.current) return null;

        const canvas = await html2canvas(ref.current, {
            scale: window.devicePixelRatio * 2,
            useCORS: true,
            allowTaint: false,
            backgroundColor: null,
        });

        return canvas;
    };

    const prepareShare = async () => {
        setIsPreparing(true);
        try {
            const canvas = await captureImage();
            setCachedCanvas(canvas);
            setDebugUrl(canvas?.toDataURL("image/png") ?? null);
        } finally {
            setIsPreparing(false);
        }
    };

    const share = async (text: string, quality: number = 0.92) => {
        try {
            if (!cachedCanvas) {
                alert("Image not ready, please try again");
                return;
            }

            const blob = await new Promise<Blob>((resolve, reject) =>
                cachedCanvas.toBlob(
                    (b) => (b ? resolve(b) : reject(new Error("Failed to create blob"))),
                    "image/jpeg",
                    quality,
                ),
            );

            const file = new File([blob], baseFileName + ".jpeg", { type: "image/jpeg" });

            if (!navigator.canShare || !navigator.canShare({ files: [file] })) {
                alert("File sharing not supported");
                return;
            }

            await navigator.share({ files: [file], text });
        } catch (error) {
            /*alert(`Share failed: ${error instanceof Error ? error.message : String(error)}`);*/
            console.error("Share error:", error);
        }
    };

    const debug = async () => {
        try {
            if (!cachedCanvas) {
                alert("Image not ready");
                return;
            }

            const dataUrl = cachedCanvas.toDataURL("image/png");
            const newTab = window.open();
            if (newTab) {
                newTab.document.write(`<img src="${dataUrl}" />`);
            } else {
                alert("Failed to open new tab (popup blocked?)");
            }
        } catch (error) {
            alert(`Debug failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    return { ref, share, prepareShare, debug, isPreparing, debugUrl };
}
